import React, { FC, ReactNode } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Box, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

export interface ExplainerSection {
  label: string;
  body: ReactNode;
  /** Renders in a warning-tinted box — use for the "must read" caveat. */
  highlight?: boolean;
}

/**
 * Collapsible "how does this work" box, in plain German for non-technical
 * readers. Bundled per screen so anyone from Marketing can open one place and
 * understand data source + calculation + meaning of every figure on it.
 *
 * Established section taxonomy — reuse it rather than inventing labels:
 * `Datengrundlage` / `Berechnung` / `Aussage` / `Wichtig`, with `highlight` on
 * the must-read caveat.
 */
const Explainer: FC<{ title: string; sections: ExplainerSection[] }> = ({ title, sections }) => (
  <Accordion
    disableGutters
    elevation={0}
    sx={{
      mb: 2,
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: '4px',
      '&:before': { display: 'none' },
      backgroundColor: 'transparent',
    }}
  >
    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <InfoOutlinedIcon sx={{ fontSize: 18, color: 'info.main' }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
      </Box>
    </AccordionSummary>
    <AccordionDetails sx={{ pt: 0 }}>
      {/* Zeilenlänge begrenzen: auf einer breiten Seite lief der Fließtext sonst
          über 290 Zeichen pro Zeile und bis in den horizontalen Überlaufbereich
          hinein, wo er abgeschnitten wurde. 78ch liegt im lesbaren Bereich. */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, maxWidth: '78ch' }}>
        {sections.map((s, i) => (
          <Box
            key={i}
            sx={
              s.highlight
                ? {
                    p: 1,
                    borderRadius: '4px',
                    backgroundColor: 'warning.light',
                    color: 'warning.contrastText',
                  }
                : undefined
            }
          >
            <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.25 }}>
              {s.label}
            </Typography>
            <Typography variant="caption" color={s.highlight ? undefined : 'text.secondary'}>
              {s.body}
            </Typography>
          </Box>
        ))}
      </Box>
    </AccordionDetails>
  </Accordion>
);

export default Explainer;
